import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef, useState } from "react";
import { MessageSquare, ClipboardList, Sparkles, LineChart, Users, ArrowRight } from "lucide-react";
import { useLanguage } from "../../context/LanguageContext";
import InterestModal from "./InterestModal";

const translations = {
  overline: { es: "Cómo trabajamos con tu organización", en: "How we work with your organization" },
  title: { es: "¿Cómo funciona?", en: "How It Works" },
  subtitle: {
    es: "Un proceso claro y colaborativo para diseñar programas de formación a la medida de tu equipo, destino o empresa.",
    en: "A clear, collaborative process to design training programs tailored to your team, destination or company.",
  },
  refBadge: { es: "Valor de referencia", en: "Reference investment" },
  refPrice: { es: "USD 3,600", en: "USD 3,600" },
  refDetail: { es: "hasta para 30 personas", en: "for up to 30 people" },
  refNote: {
    es: "Programa ajustable según duración, formato y contenido personalizado.",
    en: "Program adjustable based on duration, format and custom content.",
  },
  refCTA: { es: "Solicitar propuesta a medida", en: "Request a tailored proposal" },
  steps: {
    es: [
      {
        number: "01",
        title: "Contáctanos",
        description: "Agenda una conversación inicial y cuéntanos las necesidades de tu equipo, destino u organización.",
      },
      {
        number: "02",
        title: "Diagnóstico",
        description: "Identificamos las áreas prioritarias de formación y los objetivos estratégicos que quieres alcanzar.",
      },
      {
        number: "03",
        title: "Programa a medida",
        description: "Diseñamos una propuesta formativa alineada con tu contexto, calendario y metas de negocio.",
      },
      {
        number: "04",
        title: "Implementación e impacto",
        description: "Formamos a tu equipo y damos seguimiento a los resultados para asegurar la transferencia al puesto de trabajo.",
      },
    ],
    en: [
      {
        number: "01",
        title: "Get in touch",
        description: "Book an initial conversation and share your team, destination, or organization's training needs.",
      },
      {
        number: "02",
        title: "Diagnosis",
        description: "We identify the priority training areas and the strategic objectives you want to achieve.",
      },
      {
        number: "03",
        title: "Custom program",
        description: "We design a training proposal aligned with your context, schedule, and business goals.",
      },
      {
        number: "04",
        title: "Implementation & impact",
        description: "We train your team and follow up on results to ensure real transfer into the workplace.",
      },
    ],
  },
};

const HowItWorksSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();
  const [interestOpen, setInterestOpen] = useState(false);

  const steps = t(translations.steps);
  const icons = [MessageSquare, ClipboardList, Sparkles, LineChart];

  return (
    <section id="como-funciona" data-testid="how-it-works-section" ref={ref} className="py-24 md:py-32 bg-[#EEF1F4]">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14 md:mb-20 max-w-2xl mx-auto"
        >
          <span className="inline-flex items-center gap-2 text-[#0B1F3B] text-xs md:text-sm uppercase tracking-[0.2em] font-bold font-body mb-4">
            <span className="w-8 h-[2px] bg-[#2ED3C6]" />
            {t(translations.overline)}
            <span className="w-8 h-[2px] bg-[#2ED3C6]" />
          </span>
          <h2 data-testid="how-it-works-title" className="font-display text-3xl md:text-4xl lg:text-5xl text-[#0B1F3B] font-medium tracking-tight mb-5">
            {t(translations.title)}
          </h2>
          <p className="font-body text-[#0B1F3B]/65 text-base md:text-lg leading-relaxed">
            {t(translations.subtitle)}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => {
            const Icon = icons[index];
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                data-testid={`step-${index + 1}`}
                className="relative group"
              >
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[60%] w-full h-[2px] bg-gradient-to-r from-[#2ED3C6] to-transparent" />
                )}
                <div className="bg-white p-8 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100">
                  <div className="flex items-center gap-4 mb-6">
                    <span className="font-display text-4xl font-bold text-[#2ED3C6]">{step.number}</span>
                    <div className="w-12 h-12 bg-[#0B1F3B] rounded-lg flex items-center justify-center group-hover:bg-[#2ED3C6] transition-colors">
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <h3 className="font-display text-xl text-[#0B1F3B] font-medium mb-3">{step.title}</h3>
                  <p className="font-body text-[#0B1F3B]/70 text-base leading-relaxed">{step.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Reference investment block */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.7 }}
          data-testid="reference-investment"
          className="mt-16 md:mt-20 max-w-4xl mx-auto"
        >
          <div className="relative bg-[#0B1F3B] rounded-3xl p-8 md:p-12 overflow-hidden">
            {/* Decorative accents */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#F59E0B]/10 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#2ED3C6]/10 rounded-full blur-3xl pointer-events-none" />

            <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <span className="inline-flex items-center gap-2 text-[#2ED3C6] text-xs uppercase tracking-[0.2em] font-bold font-body mb-3">
                  <span className="w-6 h-[2px] bg-[#2ED3C6]" />
                  {t(translations.refBadge)}
                </span>
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="font-display text-5xl md:text-6xl text-white font-bold tracking-tight">
                    {t(translations.refPrice)}
                  </span>
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <Users className="w-4 h-4 text-[#F59E0B]" />
                  <span className="font-body text-white/85 text-base font-semibold">
                    {t(translations.refDetail)}
                  </span>
                </div>
                <p className="font-body text-white/60 text-sm leading-relaxed">
                  {t(translations.refNote)}
                </p>
              </div>

              <div className="flex md:justify-end">
                <button
                  type="button"
                  onClick={() => setInterestOpen(true)}
                  data-testid="reference-investment-cta"
                  className="inline-flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-[#0B1F3B] font-display font-bold py-4 px-7 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-[#F59E0B]/30 group"
                >
                  {t(translations.refCTA)}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <InterestModal open={interestOpen} onClose={() => setInterestOpen(false)} />
    </section>
  );
};

export default HowItWorksSection;
